import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, InlineAlert, Input, Label, Money, Spinner } from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';
import { formatNumber } from '@/shared/utils/number';

import type { OfflineCouponList, OfflineCouponPreview, OfflineSaleSession } from '../types';

export interface OfflineCouponPanelProps {
  session: OfflineSaleSession;
  coupons?: OfflineCouponList;
  loading?: boolean;
  busy?: boolean;
  appliedCode: string | null;
  notice: string | null;
  preview: OfflineCouponPreview | null;
  onPreview: (code: string) => Promise<void>;
  onApply: (code: string) => Promise<void>;
  onRemove: () => Promise<void>;
}

function couponWhen(value: string | null): string | null {
  if (!value || Number.isNaN(Date.parse(value))) return null;
  return formatDate(value, 'date');
}

export function OfflineCouponPanel({
  session,
  coupons,
  loading = false,
  busy = false,
  appliedCode,
  notice,
  preview,
  onPreview,
  onApply,
  onRemove,
}: OfflineCouponPanelProps) {
  const { t } = useTranslation('offlineOrders');
  const [code, setCode] = useState('');

  return (
    <section className="flex flex-col gap-3 border-t border-border pt-4">
      <h2 className="text-sm font-semibold text-fg">{t('coupons.title')}</h2>
      {session.customerRegistered ? null : (
        <p className="text-xs text-fg-muted">{t('coupons.locked')}</p>
      )}

      {session.customerRegistered ? (
        <>
          {session.couponDiscountPaise > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm">
              <span className="text-success-strong">
                {appliedCode
                  ? t('coupons.applied', { code: appliedCode })
                  : t('coupons.appliedUnknown')}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void onRemove()}
              >
                {t('coupons.remove')}
              </Button>
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor="offline-coupon-code">{t('coupons.code')}</Label>
            <Input
              id="offline-coupon-code"
              value={code}
              autoComplete="off"
              disabled={busy}
              placeholder={t('coupons.codePlaceholder')}
              onChange={(event) => setCode(event.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy || code.trim().length === 0}
                onClick={() => void onPreview(code)}
              >
                {t('coupons.preview')}
              </Button>
              <Button
                type="button"
                disabled={busy || code.trim().length === 0}
                onClick={() => void onApply(code)}
              >
                {t('coupons.apply')}
              </Button>
            </div>
          </div>

          {preview?.valid ? (
            <p className="text-sm text-success-strong" role="status">
              {preview.message ? <span>{preview.message} </span> : null}
              <Money amount={preview.discountPaise} />
              <span aria-hidden="true"> · </span>
              <Money amount={preview.totalAfterDiscountPaise} />
            </p>
          ) : null}

          {notice ? <InlineAlert>{notice}</InlineAlert> : null}

          {loading ? <Spinner label={t('coupons.title')} /> : null}

          {(coupons?.items.length ?? 0) === 0 && !loading ? (
            <p className="text-sm text-fg-muted">{t('coupons.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {coupons?.items.map((coupon) => {
                const start = couponWhen(coupon.startsAt);
                const end = couponWhen(coupon.endsAt);
                return (
                  <li key={coupon.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-fg">{coupon.code}</p>
                        {coupon.description ? (
                          <p className="text-fg-muted">{coupon.description}</p>
                        ) : null}
                        <p className="mt-1 text-success-strong">
                          {coupon.discountKind === 'PERCENTAGE' ? (
                            t('coupons.percent', {
                              value: formatNumber(coupon.discountValue, 'decimal'),
                            })
                          ) : (
                            <Money amount={coupon.discountValue} />
                          )}
                        </p>
                        {coupon.minOrderValuePaise > 0 ? (
                          <p className="text-xs text-fg-muted">
                            {t('coupons.minOrder')} <Money amount={coupon.minOrderValuePaise} />
                          </p>
                        ) : null}
                        {coupon.maxDiscountAmountPaise != null ? (
                          <p className="text-xs text-fg-muted">
                            {t('coupons.maxDiscount')}{' '}
                            <Money amount={coupon.maxDiscountAmountPaise} />
                          </p>
                        ) : null}
                        {start && end ? (
                          <p className="text-xs text-fg-muted">
                            {t('coupons.valid', { start, end })}
                          </p>
                        ) : null}
                        {coupon.isActive ? null : (
                          <p className="text-xs text-fg-muted">{t('coupons.inactive')}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={busy || !coupon.isActive}
                        onClick={() => void onApply(coupon.code)}
                      >
                        {t('coupons.apply')}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}
